import * as Dfns from "date-fns";
import { formatMoney } from "@blisspointmedia/bpm-types/dist/formatUtils";
import {
  MetaBudgetInfo,
  TestEntityType,
  CampaignCell,
  ObjectiveCell,
  MultiCellFeasibilityResult,
  LiftTestCellPayload,
  LiftTestObjectivePayload,
} from "@blisspointmedia/bpm-types/dist/MetaLiftStudy";
import { zonedTimeToUtc } from "date-fns-tz";

export interface SelectedTestOptions {
  accounts: boolean;
  campaigns: boolean;
  adSets: boolean;
}

export interface PowerTestResult {
  cellId: string;
  cellName: string;
  testPercent: number;
  controlPercent: number;
  controlImpressions: number;
  treatmentImpressions: number;
  controlSpend: number;
  treatmentSpend: number;
  totalImpressions: number;
  totalSpend: number;
  testLengthDays: number;
  isPowered: boolean;
  histDailySpend: number;
  neededDailySpend: number;
}

export interface CellMetrics {
  cellId: string;
  cellName: string;
  testPercent: number;
  controlPercent: number;
  incrementality: number;
  spend: number;
  impressions: number;
  frequency: number;
  totalConversions: number;
  cpm: number;
  rrPerMips: number;
  histDailySpend: number;
}

export interface MultiCellPowerTestResult {
  testLengthDays: number;
  avgFrequency: number;
  cellMetrics: CellMetrics[];
  feasibility: MultiCellFeasibilityResult;
  totalNeededDailySpend: number;
  totalHistDailySpend: number;
  isPowered: boolean;
}

export interface CellPowerResult {
  cellId: string;
  cellName: string;
  isPowered: boolean;
  minRequiredBudget: number;
  configurationBudget: number;
}

export const computeCellPowerResults = (result: MultiCellPowerTestResult): CellPowerResult[] => {
  const breakdownEntries = Object.values(result.feasibility.cellBreakdown);

  return result.cellMetrics.map((cell, index) => {
    const breakdown =
      result.feasibility.cellBreakdown[`Cell_${index + 1}`] ?? breakdownEntries[index];
    const configBudget = cell.histDailySpend * result.testLengthDays;
    const minRequired = breakdown?.spendEstimated ?? 0;

    return {
      cellId: cell.cellId,
      cellName: cell.cellName,
      isPowered: cell.histDailySpend > 0 && configBudget >= minRequired,
      minRequiredBudget: minRequired,
      configurationBudget: configBudget,
    };
  });
};

export interface SpendAmountJson {
  value: number;
  formatted: string;
}

export interface TotalPowerCalcResultsJson {
  title: "Total Power Calc Results";
  total_minimum_required_spend: SpendAmountJson;
  expected_spend: SpendAmountJson;
}

const buildSpendAmountJson = (amount: number): SpendAmountJson => {
  const safeAmount = Number.isFinite(amount) ? amount : 0;
  return {
    value: safeAmount,
    formatted: formatMoney(safeAmount),
  };
};

export const buildTotalPowerCalcResults = (
  totalMinimumRequiredSpend: number,
  totalExpectedSpend: number
): TotalPowerCalcResultsJson => {
  return {
    title: "Total Power Calc Results",
    total_minimum_required_spend: buildSpendAmountJson(totalMinimumRequiredSpend),
    expected_spend: buildSpendAmountJson(totalExpectedSpend),
  };
};

const getCellLabel = (index: number): string => {
  if (index >= 0 && index < 26) {
    return `Cell ${String.fromCharCode(65 + index)}`;
  }
  return `Cell ${index + 1}`;
};

export interface CellPowerCalcResultJson {
  cell_id: string;
  cell_label: string;
  cell_name: string;
  minimum_required_spend: SpendAmountJson;
  expected_spend: SpendAmountJson;
}

export interface CellToCellPowerCalcComparisonJson {
  title: "Cell-to-Cell Comparison";
  minimum_required_spend: SpendAmountJson;
  expected_spend: SpendAmountJson;
}

export interface CellLevelPowerCalcResultsJson {
  title: "Cell Level Power Calc Results";
  cells: CellPowerCalcResultJson[];
  cell_to_cell_comparison: CellToCellPowerCalcComparisonJson | null;
}

export const buildCellLevelPowerCalcResults = (
  cellResults: CellPowerResult[],
  pairwiseMinimumRequiredSpend: number,
  pairwiseExpectedSpend: number,
  includePairwiseComparison: boolean
): CellLevelPowerCalcResultsJson | null => {
  if (cellResults.length === 0) {
    return null;
  }

  const cells = cellResults.map((cellResult, index) => ({
    cell_id: cellResult.cellId,
    cell_label: getCellLabel(index),
    cell_name: cellResult.cellName?.trim() || "",
    minimum_required_spend: buildSpendAmountJson(cellResult.minRequiredBudget),
    expected_spend: buildSpendAmountJson(cellResult.configurationBudget),
  }));

  return {
    title: "Cell Level Power Calc Results",
    cells,
    cell_to_cell_comparison: includePairwiseComparison
      ? {
          title: "Cell-to-Cell Comparison",
          minimum_required_spend: buildSpendAmountJson(pairwiseMinimumRequiredSpend),
          expected_spend: buildSpendAmountJson(pairwiseExpectedSpend),
        }
      : null,
  };
};

const formatEventNameForPayload = (eventName: string): string => {
  const cleaned = eventName.replace(/^fb_pixel_/, "").replace(/_/g, " ");
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
};

export const buildObjectivesPayload = (
  objectiveCells: ObjectiveCell[],
  pixelId: string
): LiftTestObjectivePayload[] => {
  return objectiveCells.map((cell, index) => {
    const eventNames = cell.eventNames?.length
      ? cell.eventNames.map(formatEventNameForPayload)
      : undefined;
    const adspixels =
      eventNames !== undefined && eventNames.length > 0
        ? [{ id: pixelId, event_names: eventNames }]
        : undefined;
    const customconversions = cell.customConversions?.length
      ? cell.customConversions.map(id => ({ id }))
      : undefined;

    return {
      name: cell.name,
      is_primary: index === 0,
      type: "CONVERSIONS" as const,
      ...(adspixels != null && { adspixels }),
      ...(customconversions != null && { customconversions }),
    };
  });
};

export const StandardEvents = [
  { label: "View Content", value: "fb_pixel_view_content" },
  { label: "Search", value: "fb_pixel_search" },
  { label: "Add to Cart", value: "fb_pixel_add_to_cart" },
  { label: "Add to Wishlist", value: "fb_pixel_add_to_wishlist" },
  { label: "Initiate Checkout", value: "fb_pixel_initiate_checkout" },
  { label: "Add Payment Info", value: "fb_pixel_add_payment_info" },
  { label: "Purchase", value: "fb_pixel_purchase" },
  { label: "Lead", value: "fb_pixel_lead" },
  { label: "Complete Registration", value: "fb_pixel_complete_registration" },
];

export const getActiveTestType = (selectedTestOptions: SelectedTestOptions): TestEntityType => {
  if (selectedTestOptions.accounts) {
    return "accounts";
  }
  if (selectedTestOptions.campaigns) {
    return "campaigns";
  }
  if (selectedTestOptions.adSets) {
    return "adSets";
  }
  throw new Error("No test type selected");
};

export const getTestTypeLabels = (
  testType: TestEntityType
): {
  singular: string;
  plural: string;
  headerQuestion: string;
  selectedLabel: string;
  noSelectionLabel: string;
  availableLabel: string;
  nameColumn: string;
  statusColumn: string;
} => {
  switch (testType) {
    case "accounts":
      return {
        singular: "Account",
        plural: "Accounts",
        headerQuestion: "Which Accounts do you want to test?*",
        selectedLabel: "Accounts",
        noSelectionLabel: "No accounts selected",
        availableLabel: "Accounts Available to Test",
        nameColumn: "Account Name",
        statusColumn: "Account Status",
      };
    case "adSets":
      return {
        singular: "Ad Set",
        plural: "Ad Sets",
        headerQuestion: "Which Ad Sets do you want to test?*",
        selectedLabel: "Ad Sets",
        noSelectionLabel: "No ad sets selected",
        availableLabel: "Ad Sets Available to Test",
        nameColumn: "Ad Set Name",
        statusColumn: "Ad Set Status",
      };
    case "campaigns":
    default:
      return {
        singular: "Campaign",
        plural: "Campaigns",
        headerQuestion: "Which Campaigns do you want to test?*",
        selectedLabel: "Campaigns",
        noSelectionLabel: "No campaigns selected",
        availableLabel: "Campaigns Available to Test",
        nameColumn: "Campaign Name",
        statusColumn: "Status",
      };
  }
};

export const getEntityDataFromCampaignData = (
  data: MetaBudgetInfo,
  testType: TestEntityType
): {
  id: string;
  name: string;
  status: string;
  accountId: string;
  budget: string;
  budgetType?: string;
  dateRange?: string;
} => {
  switch (testType) {
    case "accounts":
      return {
        id: data.account_id,
        name: data.account_name,
        status: data.account_status,
        accountId: data.account_id,
        budget: data.account_daily_budget || "N/A",
      };
    case "adSets":
      return {
        id: data.adset_id,
        name: data.adset_name,
        status: data.adset_status,
        accountId: data.account_id,
        budget:
          data.adset_daily_budget && data.adset_daily_budget !== "0"
            ? data.adset_daily_budget
            : data.adset_lifetime_budget && data.adset_lifetime_budget !== "0"
            ? data.adset_lifetime_budget
            : "N/A",
        budgetType:
          data.adset_daily_budget && data.adset_daily_budget !== "0"
            ? "daily"
            : data.adset_lifetime_budget && data.adset_lifetime_budget !== "0"
            ? "lifetime"
            : "N/A",
      };
    case "campaigns":
    default:
      return {
        id: data.campaign_id,
        name: data.campaign_name,
        status: data.campaign_status,
        accountId: data.account_id,
        budget:
          data.campaign_daily_budget && data.campaign_daily_budget !== "0"
            ? data.campaign_daily_budget
            : data.campaign_lifetime_budget && data.campaign_lifetime_budget !== "0"
            ? data.campaign_lifetime_budget
            : "N/A",
        budgetType:
          data.campaign_daily_budget && data.campaign_daily_budget !== "0"
            ? "daily"
            : data.campaign_lifetime_budget
            ? "lifetime"
            : "N/A",
      };
  }
};

export interface UniqueEntity {
  id: string;
  name: string;
  status: string;
  accountId: string;
  budget: string;
  budgetType?: string;
  dateRange?: string;
}

export const getUniqueEntities = (
  campaignOptions: MetaBudgetInfo[],
  testType: TestEntityType
): UniqueEntity[] => {
  if (!testType) {
    return [];
  }

  const entityMap = new Map<string, UniqueEntity>();

  campaignOptions.forEach(data => {
    const entity = getEntityDataFromCampaignData(data, testType);
    if (!entityMap.has(entity.id)) {
      if (testType === "adSets") {
        const budgetValue = parseBudgetValue(entity.budget);
        if (budgetValue === 0) {
          return;
        }
      } else if (testType === "campaigns") {
        const budgetValue = parseBudgetValue(entity.budget);
        if (budgetValue === 0) {
          entity.budget = "\u2014\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0";
        }
      }
      entityMap.set(entity.id, entity);
    }
  });

  return Array.from(entityMap.values());
};

export const isoDateToUnixTimestamp = (isoDate: string | undefined): string | undefined => {
  if (!isoDate || isoDate === "") {
    return undefined;
  }

  try {
    const date = Dfns.parseISO(isoDate);

    if (!Dfns.isValid(date)) {
      return undefined;
    }

    const timestampSeconds = Math.floor(date.getTime() / 1000);
    return timestampSeconds.toString();
  } catch (e) {
    throw new Error(`Error converting ISO date to Unix timestamp: ${e}`);
  }
};

export const TIMEZONE_OPTIONS = ["Pacific", "Mountain", "Central", "Eastern"] as const;
export type TimezoneOption = "Pacific" | "Mountain" | "Central" | "Eastern";

const TIMEZONE_TO_IANA: Record<TimezoneOption, string> = {
  Pacific: "America/Los_Angeles",
  Mountain: "America/Denver",
  Central: "America/Chicago",
  Eastern: "America/New_York",
};

export const dateTimeToUnixTimestamp = (
  date: string | undefined,
  time: string | undefined,
  zone: TimezoneOption | undefined
): number | undefined => {
  if (!date || !time || !zone) {
    return undefined;
  }

  const full = `${date}T${time}:00`;
  const utc = zonedTimeToUtc(full, TIMEZONE_TO_IANA[zone]);
  return Math.floor(utc.getTime() / 1000);
};

export const unixTimestampToIsoDate = (timestamp: string | undefined): string => {
  if (!timestamp || timestamp === "") {
    return "";
  }

  try {
    const timestampNum = parseInt(timestamp, 10);
    if (isNaN(timestampNum)) {
      return "";
    }

    const date = new Date(timestampNum * 1000);

    if (isNaN(date.getTime())) {
      return "";
    }

    return Dfns.format(date, "yyyy-MM-dd");
  } catch (error) {
    throw new Error(`Error converting Unix timestamp to ISO date: ${error}`);
  }
};

const parseBudgetValue = (budget: string | undefined): number => {
  if (!budget || budget === "N/A") {
    return 0;
  }
  const parsed = parseFloat(budget);
  return isNaN(parsed) ? 0 : parsed;
};

export const buildEntityMap = (
  campaignOptions: MetaBudgetInfo[],
  testType: TestEntityType
): Map<string, UniqueEntity> => {
  const entityMap = new Map<string, UniqueEntity>();

  for (const option of campaignOptions) {
    const entity = getEntityDataFromCampaignData(option, testType);
    if (!entityMap.has(entity.id)) {
      entityMap.set(entity.id, entity);
    }
  }

  return entityMap;
};

export const calculateTotalBudget = (
  campaignCells: CampaignCell[],
  campaignOptions: MetaBudgetInfo[],
  selectedTestOptions: SelectedTestOptions
): string => {
  if (!campaignOptions.length || !campaignCells.length) {
    return "$0";
  }

  let testType: TestEntityType;
  try {
    testType = getActiveTestType(selectedTestOptions);
  } catch {
    return "$0";
  }

  const entityMap = buildEntityMap(campaignOptions, testType);

  let totalBudgetInCents = 0;

  for (const cell of campaignCells) {
    for (const selectedItem of cell.selectedItems) {
      const entity = entityMap.get(selectedItem.id);
      if (entity) {
        totalBudgetInCents += parseBudgetValue(entity.budget);
      }
    }
  }

  if (totalBudgetInCents === 0) {
    return "$0";
  }

  return `${formatMoney(totalBudgetInCents / 100, 0)} Daily`;
};

const TEST_TYPE_TO_CELL_KEY: Record<TestEntityType, string> = {
  accounts: "adaccounts",
  campaigns: "campaigns",
  adSets: "adsets",
};

export interface LiftTestPayload {
  company: string;
  user_email: string;
  budget: number;
  account: string;
  name: string;
  description: string;
  start_time: number;
  end_time: number;
  cooldown_start_time: number;
  observation_end_time: number;
  type: "LIFT";
  cells: Record<string, LiftTestCellPayload>;
  objectives: LiftTestObjectivePayload[];
  feasibility: boolean;
  powered: boolean;
  total_power_calc_results: TotalPowerCalcResultsJson | null;
  cell_level_power_calc_results: CellLevelPowerCalcResultsJson | null;
}

export interface BuildLiftTestPayloadParams {
  company: string;
  userEmail: string;
  budget: number;
  accountId: string;
  testName: string;
  testDescription: string;
  startTime: number;
  endTime: number;
  testType: TestEntityType;
  campaignCells: CampaignCell[];
  objectiveCells: ObjectiveCell[];
  pixelId: string;
  feasibility?: boolean;
  powered?: boolean;
  totalPowerCalcResults?: TotalPowerCalcResultsJson | null;
  cellLevelPowerCalcResults?: CellLevelPowerCalcResultsJson | null;
}

export const getCellShare = (cell: CampaignCell): number =>
  cell.testPercentage + cell.controlPercentage;

export const getCellsTotalSum = (cells: CampaignCell[]): number =>
  cells.reduce((sum, c) => sum + getCellShare(c), 0);

const DEFAULT_TEST_RATIO = 0.8;

export const applyRemainderCell = (
  cells: CampaignCell[],
  remainderIndex: number
): CampaignCell[] => {
  if (cells.length === 0) {
    return cells;
  }
  const sumOthers = cells.reduce(
    (acc, c, i) => (i === remainderIndex ? acc : acc + getCellShare(c)),
    0
  );
  const remainderShare = 1 - sumOthers;
  const remainderCell = cells[remainderIndex];
  const total = getCellShare(remainderCell);
  const ratio = total > 0 ? remainderCell.testPercentage / total : DEFAULT_TEST_RATIO;
  const newTest = Math.max(0, Math.min(1, remainderShare * ratio));
  const newControl = Math.max(0, Math.min(1, remainderShare - newTest));
  return cells.map((cell, i) =>
    i === remainderIndex
      ? { ...cell, testPercentage: newTest, controlPercentage: newControl }
      : cell
  );
};

const DEFAULT_CELL_SPLITS: Record<string, { test: number; control: number }> = {
  1: { test: 0.9, control: 0.1 },
  2: { test: 0.45, control: 0.05 },
  3: { test: 0.3033, control: 0.03 },
  4: { test: 0.22, control: 0.03 },
  5: { test: 0.18, control: 0.02 },
};

export const applyDefaultSplitsToCells = (cells: CampaignCell[]): CampaignCell[] => {
  const n = cells.length;
  if (n === 0) {
    return cells;
  }

  const split = DEFAULT_CELL_SPLITS[n] ?? {
    test: (1 / n) * DEFAULT_TEST_RATIO,
    control: (1 / n) * (1 - DEFAULT_TEST_RATIO),
  };

  const updated = cells.map(cell => ({
    ...cell,
    testPercentage: split.test,
    controlPercentage: split.control,
  }));

  return applyRemainderCell(updated, n - 1);
};

const stripActPrefix = (id: string): string => {
  return id.replace(/^act_/, "");
};

const buildCellPayload = (cell: CampaignCell, testType: TestEntityType): LiftTestCellPayload => {
  const entityKey = TEST_TYPE_TO_CELL_KEY[testType];
  const entityIds = cell.selectedItems.map(item => stripActPrefix(item.id));

  return {
    name: cell.name,
    description: `Cell containing ${entityIds.length} ${testType}`,
    treatment_percentage: Math.round(cell.testPercentage * 100),
    control_percentage: Math.round(cell.controlPercentage * 100),
    [entityKey]: entityIds,
  };
};

export const buildLiftTestPayload = ({
  company,
  accountId,
  userEmail,
  budget,
  testName,
  testDescription,
  startTime,
  endTime,
  testType,
  campaignCells,
  objectiveCells,
  pixelId,
  feasibility = true,
  powered = false,
  totalPowerCalcResults = null,
  cellLevelPowerCalcResults = null,
}: BuildLiftTestPayloadParams): LiftTestPayload => {
  const cells: Record<string, LiftTestCellPayload> = {};
  campaignCells.forEach((cell, index) => {
    cells[`cell_${index + 1}`] = buildCellPayload(cell, testType);
  });

  return {
    company,
    user_email: userEmail,
    budget,
    account: accountId.startsWith("act_") ? accountId : `act_${accountId}`,
    name: testName,
    description: testDescription,
    start_time: startTime,
    end_time: endTime,
    cooldown_start_time: startTime,
    observation_end_time: endTime,
    type: "LIFT",
    cells,
    objectives: buildObjectivesPayload(objectiveCells, pixelId),
    feasibility,
    powered,
    total_power_calc_results: totalPowerCalcResults,
    cell_level_power_calc_results: cellLevelPowerCalcResults,
  };
};

export const formatEventName = (eventName: string): string => {
  const cleaned = eventName.replace(/^fb_pixel_/, "").replace(/_/g, " ");
  return cleaned
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const formatEventNames = (eventNames: string[]): string => {
  if (!eventNames || eventNames.length === 0) {
    return "No events selected";
  }
  return eventNames.map(formatEventName).join(", ");
};
